import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Clock, MapPin, Ban, Pencil, Trash2, X } from 'lucide-react';
import type { Event, MemberUnavailability, BandMember } from '../../types/database';
import { formatTime } from '../../lib/utils';

interface Props {
  events: Event[];
  unavailability: MemberUnavailability[];
  members: BandMember[];
  currentUserId?: string;
  isAdmin?: boolean;
  onSelectEvent: (event: Event) => void;
  onDeleteEvent: (eventId: string) => void;
  onDeleteUnavailability: (id: string) => void;
  onSelectDate: (date: string) => void;
}

function getMonthDays(year: number, month: number) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startPad = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
  const days: Date[] = [];
  for (let i = startPad - 1; i >= 0; i--) days.push(new Date(year, month, -i));
  for (let i = 1; i <= lastDay.getDate(); i++) days.push(new Date(year, month, i));
  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) days.push(new Date(year, month + 1, i));
  return days;
}

function getWeekDays(date: Date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diff);
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(monday);
    day.setDate(monday.getDate() + i);
    days.push(day);
  }
  return days;
}

function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function formatTimeShort(dateStr: string) {
  return new Date(dateStr).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function toDateKey(d: Date) {
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function BandCalendarView({ events, unavailability, members, currentUserId, isAdmin, onSelectEvent, onDeleteEvent, onDeleteUnavailability, onSelectDate }: Props) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [view, setView] = useState<'month' | 'week'>('month');
  const today = new Date();

  const eventsByDate = useMemo(() => {
    const map = new Map<string, Event[]>();
    for (const event of events) {
      const key = new Date(event.start_time).toDateString();
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(event);
    }
    return map;
  }, [events]);

  // Build a map of date -> unavailable members
  const unavailByDate = useMemo(() => {
    const map = new Map<string, MemberUnavailability[]>();
    for (const u of unavailability) {
      const start = new Date(u.start_date + 'T00:00:00');
      const end = new Date(u.end_date + 'T00:00:00');
      const cursor = new Date(start);
      while (cursor <= end) {
        const key = cursor.toDateString();
        if (!map.has(key)) map.set(key, []);
        const arr = map.get(key)!;
        if (!arr.find((x) => x.id === u.id)) arr.push(u);
        cursor.setDate(cursor.getDate() + 1);
      }
    }
    return map;
  }, [unavailability]);

  function navigate(dir: -1 | 1) {
    const d = new Date(currentDate);
    if (view === 'month') d.setMonth(d.getMonth() + dir);
    else d.setDate(d.getDate() + dir * 7);
    setCurrentDate(d);
  }

  function goToday() {
    setCurrentDate(new Date());
    setSelectedDate(null);
  }

  function handleDateClick(day: Date) {
    setSelectedDate(day);
  }

  function getMemberName(userId: string) {
    const member = members.find((m) => m.user_id === userId);
    return member?.display_name || member?.profile?.display_name || member?.email || 'Unknown';
  }

  function getMemberInstruments(userId: string) {
    const member = members.find((m) => m.user_id === userId);
    return member?.instruments ?? [];
  }

  const selectedEvents = selectedDate ? eventsByDate.get(selectedDate.toDateString()) ?? [] : [];
  const selectedUnavail = selectedDate ? unavailByDate.get(selectedDate.toDateString()) ?? [] : [];
  // Deduplicate unavailable members by user_id
  const uniqueUnavail = selectedUnavail.filter(
    (u, i, arr) => arr.findIndex((x) => x.user_id === u.user_id) === i
  );

  const headerLabel = view === 'month'
    ? currentDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
    : (() => {
        const days = getWeekDays(currentDate);
        return `${days[0].toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – ${days[6].toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
      })();

  function renderDayCell(day: Date, isCurrentMonth: boolean) {
    const isToday = sameDay(day, today);
    const dayEvents = eventsByDate.get(day.toDateString()) ?? [];
    const dayUnavail = unavailByDate.get(day.toDateString()) ?? [];
    const uniqueDayUnavail = dayUnavail.filter(
      (u, i, arr) => arr.findIndex((x) => x.user_id === u.user_id) === i
    );
    const isSelected = selectedDate && sameDay(day, selectedDate);
    const hasUnavail = uniqueDayUnavail.length > 0;

    return (
      <button
        key={day.toISOString()}
        onClick={() => handleDateClick(day)}
        className={`relative min-h-[80px] sm:min-h-[100px] p-1 sm:p-2 text-left border-b border-r border-gray-800 transition hover:bg-gray-800/50 ${
          !isCurrentMonth ? 'opacity-30' : ''
        } ${isSelected ? 'bg-indigo-900/20 ring-1 ring-indigo-500' : ''} ${
          hasUnavail && isCurrentMonth ? 'bg-red-900/10' : ''
        }`}
      >
        <div className="flex items-center justify-between">
          <span className={`text-xs sm:text-sm font-medium ${
            isToday ? 'bg-indigo-600 text-white w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center' : 'text-gray-400'
          }`}>
            {day.getDate()}
          </span>
          {hasUnavail && isCurrentMonth && (
            <span className="flex items-center gap-0.5 text-[10px] text-red-400" title={`${uniqueDayUnavail.length} member${uniqueDayUnavail.length > 1 ? 's' : ''} unavailable`}>
              <Ban className="w-3 h-3" />
              <span className="hidden sm:inline">{uniqueDayUnavail.length}</span>
            </span>
          )}
        </div>
        {dayEvents.length > 0 && (
          <div className="mt-1 space-y-0.5">
            {dayEvents.slice(0, 2).map((ev) => (
              <div
                key={ev.id}
                className={`border-l-2 text-[10px] sm:text-xs px-1 py-0.5 rounded-r truncate ${
                  ev.event_type === 'gig'
                    ? 'bg-indigo-900/40 border-indigo-500 text-indigo-300'
                    : ev.event_type === 'rehearsal'
                      ? 'bg-emerald-900/40 border-emerald-500 text-emerald-300'
                      : 'bg-gray-600/30 border-gray-500 text-gray-300'
                }`}
              >
                <span className="hidden sm:inline">{formatTimeShort(ev.start_time)} </span>
                {ev.title}
              </div>
            ))}
            {dayEvents.length > 2 && (
              <div className="text-[10px] text-indigo-400 px-1">+{dayEvents.length - 2} more</div>
            )}
          </div>
        )}
        {hasUnavail && isCurrentMonth && dayEvents.length === 0 && (
          <div className="mt-1">
            <div className="text-[10px] text-red-400/70 px-1 truncate">
              {uniqueDayUnavail.map((u) => getMemberName(u.user_id).split(' ')[0]).join(', ')}
            </div>
          </div>
        )}
      </button>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg hover:bg-gray-800 transition text-gray-400 hover:text-gray-50">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h3 className="text-lg font-semibold min-w-[200px] text-center">{headerLabel}</h3>
          <button onClick={() => navigate(1)} className="p-1.5 rounded-lg hover:bg-gray-800 transition text-gray-400 hover:text-gray-50">
            <ChevronRight className="w-5 h-5" />
          </button>
          <button onClick={goToday} className="ml-2 text-xs bg-gray-800 hover:bg-gray-700 text-gray-300 px-3 py-1 rounded-lg transition">
            Today
          </button>
        </div>
        <div className="flex items-center gap-3">
          {/* Legend */}
          <div className="hidden sm:flex items-center gap-3 text-[10px] text-gray-500 mr-2">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-indigo-500"></span>Gig</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"></span>Rehearsal</span>
            <span className="flex items-center gap-1"><Ban className="w-2.5 h-2.5 text-red-400" />Unavailable</span>
          </div>
          <div className="flex bg-gray-800 rounded-lg p-0.5 text-sm">
            {(['month', 'week'] as const).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3 py-1.5 rounded-md font-medium transition capitalize ${
                  view === v ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Month view */}
      {view === 'month' && (
        <div className="border border-gray-800 rounded-xl overflow-hidden">
          <div className="grid grid-cols-7 bg-gray-900">
            {WEEKDAY_LABELS.map((d) => (
              <div key={d} className="text-center text-xs font-semibold text-gray-500 py-2 border-b border-gray-800">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {getMonthDays(currentDate.getFullYear(), currentDate.getMonth()).map((day) =>
              renderDayCell(day, day.getMonth() === currentDate.getMonth())
            )}
          </div>
        </div>
      )}

      {/* Week view */}
      {view === 'week' && (
        <div className="border border-gray-800 rounded-xl overflow-hidden">
          <div className="grid grid-cols-7">
            {getWeekDays(currentDate).map((day, i) => {
              const isToday = sameDay(day, today);
              const dayEvents = eventsByDate.get(day.toDateString()) ?? [];
              const dayUnavail = unavailByDate.get(day.toDateString()) ?? [];
              const uniqueDayUnavail = dayUnavail.filter(
                (u, idx, arr) => arr.findIndex((x) => x.user_id === u.user_id) === idx
              );
              const isSelected = selectedDate && sameDay(day, selectedDate);
              const hasUnavail = uniqueDayUnavail.length > 0;

              return (
                <button
                  key={i}
                  onClick={() => handleDateClick(day)}
                  className={`min-h-[200px] p-2 text-left border-r border-gray-800 last:border-r-0 transition hover:bg-gray-800/50 ${
                    isSelected ? 'bg-indigo-900/20 ring-1 ring-indigo-500' : ''
                  } ${hasUnavail ? 'bg-red-900/10' : ''}`}
                >
                  <div className="text-center mb-2">
                    <div className="text-[10px] uppercase text-gray-500 font-semibold">{WEEKDAY_LABELS[i]}</div>
                    <div className={`text-sm font-medium mt-0.5 ${
                      isToday ? 'bg-indigo-600 text-white w-7 h-7 rounded-full flex items-center justify-center mx-auto' : 'text-gray-300'
                    }`}>
                      {day.getDate()}
                    </div>
                  </div>
                  {hasUnavail && (
                    <div className="mb-1 bg-red-900/30 border border-red-500/20 rounded px-1.5 py-1 text-[10px] text-red-300">
                      <div className="flex items-center gap-1 font-medium">
                        <Ban className="w-2.5 h-2.5" />
                        {uniqueDayUnavail.length} unavailable
                      </div>
                      {uniqueDayUnavail.map((u) => (
                        <div key={u.id} className="text-red-400/70 truncate">
                          {getMemberName(u.user_id).split(' ')[0]}
                          {u.reason && ` - ${u.reason}`}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="space-y-1">
                    {dayEvents.map((ev) => (
                      <div
                        key={ev.id}
                        className={`border-l-2 text-[10px] sm:text-xs px-1.5 py-1 rounded-r ${
                          ev.event_type === 'gig'
                            ? 'bg-indigo-900/40 border-indigo-500 text-indigo-300'
                            : ev.event_type === 'rehearsal'
                              ? 'bg-emerald-900/40 border-emerald-500 text-emerald-300'
                              : 'bg-gray-600/30 border-gray-500 text-gray-300'
                        }`}
                      >
                        <div className="font-medium truncate">{ev.title}</div>
                        <div className="text-gray-400">{formatTimeShort(ev.start_time)}</div>
                      </div>
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Selected date detail panel */}
      {selectedDate && (
        <div className="mt-4 bg-gray-900 border border-gray-800 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wide">
              {selectedDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </h4>
            <button
              onClick={() => onSelectDate(toDateKey(selectedDate))}
              className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded-lg transition"
            >
              + Add Event
            </button>
          </div>

          {/* Unavailability warnings */}
          {uniqueUnavail.length > 0 && (
            <div className="mb-4 bg-red-900/20 border border-red-500/20 rounded-lg p-3">
              <div className="flex items-center gap-1.5 text-red-400 text-xs font-semibold uppercase tracking-wide mb-2">
                <Ban className="w-3.5 h-3.5" />
                Unavailable Members
              </div>
              <div className="space-y-1.5">
                {uniqueUnavail.map((u) => {
                  const canDelete = u.user_id === currentUserId || isAdmin;
                  const dateRange = u.start_date === u.end_date
                    ? new Date(u.start_date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                    : `${new Date(u.start_date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – ${new Date(u.end_date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
                  return (
                    <div key={u.id} className="flex items-center justify-between gap-2 text-sm">
                      <div className="flex-1 min-w-0">
                        <span className="text-gray-50 font-medium">{getMemberName(u.user_id)}</span>
                        {getMemberInstruments(u.user_id).length > 0 && (
                          <span className="text-red-300 ml-2 text-xs">
                            ({getMemberInstruments(u.user_id).join(', ')})
                          </span>
                        )}
                        <div className="text-xs text-gray-500 mt-0.5">
                          {dateRange}
                          {u.reason && <> &middot; {u.reason}</>}
                        </div>
                      </div>
                      {canDelete && (
                        <button
                          onClick={() => onDeleteUnavailability(u.id)}
                          className="text-gray-500 hover:text-red-400 transition p-1 flex-shrink-0"
                          title="Remove unavailability"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Events on this date */}
          {selectedEvents.length === 0 ? (
            <p className="text-gray-500 text-sm py-2 text-center">No events on this day</p>
          ) : (
            <div className="space-y-2">
              {selectedEvents.map((ev) => (
                <div
                  key={ev.id}
                  className="flex items-center gap-2 p-3 rounded-lg hover:bg-gray-800 transition"
                >
                  <button
                    onClick={() => onSelectEvent(ev)}
                    className="flex items-start gap-3 flex-1 min-w-0 text-left"
                  >
                    <div className={`w-1 self-stretch rounded-full flex-shrink-0 ${
                      ev.event_type === 'gig' ? 'bg-indigo-400' : ev.event_type === 'rehearsal' ? 'bg-emerald-400' : 'bg-gray-500'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h5 className="font-medium text-gray-50 truncate">{ev.title}</h5>
                        <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                          ev.event_type === 'gig' ? 'bg-indigo-900/50 text-indigo-300'
                            : ev.event_type === 'rehearsal' ? 'bg-emerald-900/50 text-emerald-300'
                              : 'bg-gray-800 text-gray-400'
                        }`}>{ev.event_type}</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-400 text-sm mt-0.5">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatTimeShort(ev.start_time)}
                          {ev.end_time && ` – ${formatTime(ev.end_time)}`}
                        </span>
                        {ev.venue_name && (
                          <span className="flex items-center gap-1 truncate">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            {ev.venue_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => onSelectEvent(ev)}
                      className="text-gray-500 hover:text-indigo-400 transition p-1.5"
                      title="Edit event"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => onDeleteEvent(ev.id)}
                        className="text-gray-500 hover:text-red-400 transition p-1.5"
                        title="Delete event"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
